import { ContractTypes } from "@common/js/enum";
import * as signalR from "@microsoft/signalr";
import * as msgpack from "@microsoft/signalr-protocol-msgpack";
import store from "@common/js/store";
import { guid } from "./utils";

class SignalMsgBody {
	constructor(msgNo, contentType, content) {
		this.ContentType = contentType;
		this.MsgNo = msgNo; // 消息号
		this.Content = []; // 消息内容
		if (content != null) {
			this.Content.push(content);
		}
	}

	GetMessage() {
		const data = {
			ContentType: this.ContentType,
			MsgNo: this.MsgNo,
			Content: [],
		};
		for (const element of this.Content) {
			if (element.GetMessage) {
				data.Content.push(element.GetMessage());
			} else {
				data.Content.push(element);
			}
		}
		return data;
	}
}

class JsonMsg {
	constructor(data) {
		this.Data = data;
	}

	GetMessage() {
		const data = {
			Data: JSON.stringify(this.Data),
		};
		return data;
	}
}

class SignalMsg {
	constructor(data) {
		this.ProtocolName = "CMA_MSG";
		this.Version = "1.0";
		this.Content = [data];
	}

	static CreateMsgNo() {
		return guid();
	}

	GetMessage() {
		var data = {
			ProtocolName: this.ProtocolName,
			Version: this.Version,
			Content: new Array(),
		};
		for (let item of this.Content) {
			data.Content.push(item.GetMessage());
		}
		return data;
	}
}

class ClientAuthenticateMsg {
	constructor(data) {
		this.User = null;
		this.Token = null;
	}

	GetMessage() {
		const data = {
			User: this.User,
			Token: this.Token,
		};
		return data;
	}
}

class ISignalRHandler {
	constructor() {
		this.SignalR = null;
		this.SupportContentType = []; // 协议类型
	}

	Dispose() {
		if (this.SignalR) {
			this.SignalR.Remove(this);
			this.Abort();
		}
	}

	// 第一次连接成功时
	OnConnected() { }
	// SignalR断线时触发
	OnDisconnected() { }
	// SignalR重连时
	OnReconnected() { }
	// SignalR有数据接收到时,data对应具体的JsonMsg对象
	ReceiveData(data) { }
	// 是否支持某种消息类型的处理
	IsSupport(contentType) {
		return this.SupportContentType.includes(contentType);
	}
	// 功能退出时
	Abort() { }
}

class SignalrClient {
	constructor(host, apihost) {
		this.HandlerList = [];
		this.Host = host;
		this.WebApi = apihost;
		this.SignalrHubId = "";
		this.LoginSuccess = false; // 连接时登录验证成功
		this.OnConnectedHandler = null;
		this.Hub = new signalR.HubConnectionBuilder()
			.withUrl(host)
			.withAutomaticReconnect([0, 3000, 5000, 10000, 15000, 30000, 45000, 60000, 75000, 90000, 120000, 150000, 180000])
			.withHubProtocol(new msgpack.MessagePackHubProtocol())
			.build();
		this.Hub.serverTimeoutInMilliseconds = 20000;
		this.Hub.keepAliveIntervalInMillisecods = 5000;
	}

	initial() {
		const sender = this;
		if (this.Hub == null) {
			return;
		}
		this.Hub.on("ReceiveData", function (data) {
			for (let content of data.Content) {
				for (let handler of sender.HandlerList) {
					if (handler.IsSupport(content.ContentType)) {
						handler.ReceiveData(content);
					}
				}
			}
		});

		this.Hub.onreconnecting((error) => {
			sender.ConnectionState = signalR.HubConnectionState.Reconnecting;
			console.log(error);
		});
		this.Hub.onreconnected(function () {
			console.log(" reconnected state:" + sender.Hub.state);
			if (sender.ConnectionState === signalR.HubConnectionState.Connected) {
				return;
			}
			sender.ConnectionState = signalR.HubConnectionState.Connected;

			let loginHandlers = sender.HandlerList.filter((r) => r.IsSupport(ContractTypes.LoginResult));
			for (let handler of loginHandlers) {
				handler.OnReconnected();
			}

			setTimeout(function () {
				let otherHandlers = sender.HandlerList.filter((r) => !r.IsSupport(ContractTypes.LoginResult));
				for (let handler of otherHandlers) {
					handler.OnReconnected();
				}
			}, 1000);
		});

		this.Hub.onclose(function () {
			console.log("signalr connection colsed");
			sender.ConnectionState = signalR.HubConnectionState.Disconnected;
			// 断连后，通知各个消息处理器
			for (let handler of sender.HandlerList) {
				handler.OnDisconnected();
			}
		});
	}

	Start() {
		this.Hub.start().then(() => {
			this.SignalrHubId = this.Hub.connection.connectionId;
			this.ConnectionState = signalR.HubConnectionState.Connected;
			console.log("signalR start success!");
			for (let handler of this.HandlerList) {
				handler.OnConnected();
			}
			if (this.OnConnectedHandler != null) {
				this.OnConnectedHandler();
			}
		}).catch((ex) => {
			console.log("error:" + ex);
			if (this.ConnectionState !== signalR.HubConnectionState.Connected) {
				setTimeout(() => this.Start(), 5000);
			}
		});
	}
	Stop() {
		let array = [...this.HandlerList];
		for (let handler of array) {
			handler.Dispose();
		}
		this.Hub.stop();
	}
	resetConnection(sender) {
		sender.Hub.connection.stop();
		sender.serverStart();
	}
	// 登录
	Login(token) {
		try {
			const msgNo = SignalMsg.CreateMsgNo();
			const contract = new SignalMsg(msgNo, ContractTypes.Login, token);
			this.Hub.server.logIn(contract.getMessage());
		} catch (e) {
			console.log(e.message);
		}
	}
	// 添加消息处理器
	Add(handler) {
		this.HandlerList.push(handler);
		handler.SignalR = this;
	}
	// 移除某个数据处理器
	Remove(handler) {
		var index = this.HandlerList.indexOf(handler);
		if (index >= 0) {
			this.HandlerList.splice(index, 1);
		}
	}
	// 获取数据处理器
	GetHandlers(supportContentType) {
		this.HandlerList.forEach(function (handler) {
			if (handler.SupportContentType.contains(supportContentType)) {
				return handler;
			}
		});
		return null;
	}
	// 发送数据
	SendData(data) {
		if (this.Hub.state === signalR.HubConnectionState.Connected) {
			// handler.SignalR.Hub.server.receiveData(data);
			this.Hub.invoke("ReceiveData", data).catch(function (err) {
				return console.error(err.toString());
			});
		}
	}
	// 功能退出时，向后台发送消息
	FunctionLogout(data) {
		if (this.Hub.state === signalR.HubConnectionState.Connected) {
			this.Hub.invoke("FunctionLogout", data).catch(function (err) {
				return console.error(err.toString());
			});
		}
	}
}

class SignalRLoginHandler extends ISignalRHandler {
	constructor() {
		super();
		this.Type = "Login";
		this.SupportContentType = [ContractTypes.LoginResult];
	}
	ReceiveData(data) {
		//data对应SignalMsg<ServerAuthenticateMsg>
		if (data.Content[0].IsAuthenticate === true) {
			this.SignalR.LoginSuccess = true;
		} else {
			// LoginManage.PopTurnToLogin(data.Content[0].Error);
		}
	}
	//第一次连接成功时
	OnConnected() {
		const user = store.state.userInfo;
		const msg = new ClientAuthenticateMsg();
		msg.User = user.UserName;
		msg.Token = user.Token;

		const msgNo = SignalMsg.CreateMsgNo();
		const jsonmsg = new JsonMsg(msg);
		const body = new SignalMsgBody(msgNo, ContractTypes.Login, jsonmsg);
		const contract = new SignalMsg(body);

		this.SignalR.SendData(contract.GetMessage());
	}
	OnReconnected() {
		this.OnConnected();
	}
}

class SignalRInstance {
	static _instance = null;
	static GetInstance() {
		return SignalRInstance._instance;
	}
	static initial(host, webapi) {
		if (SignalRInstance._instance == null) {
			SignalRInstance._instance = new SignalrClient(host, webapi);
			const instance = SignalRInstance._instance;
			instance.Add(new SignalRLoginHandler());
			instance.initial();
			instance.Start();
		}
	}
	static Clear() {
		SignalRInstance._instance.Stop();
		SignalRInstance._instance = null;
	}
}

export { ISignalRHandler, SignalRInstance };
